import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trophy, Flame, Wallet, ExternalLink, Sparkles, Award } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const T = {
  es: {
    title: "¡Capítulo completado!",
    reward: "Recompensa total",
    you: "Recibes tú (95%)",
    burned: "Quemados para siempre (5%)",
    pending: "R₿L pendientes",
    claim_later: "Reclama todo desde tu perfil",
    close: "Cerrar",
    achievements: "Logros desbloqueados",
    bonus: "bonus",
    next: "Siguiente capítulo →",
  },
  en: {
    title: "Chapter completed!",
    reward: "Total reward",
    you: "You receive (95%)",
    burned: "Burned forever (5%)",
    pending: "Pending R₿L",
    claim_later: "Claim everything from your profile",
    close: "Close",
    achievements: "Achievements unlocked",
    bonus: "bonus",
    next: "Next chapter →",
  },
  sk: {
    title: "Kapitola dokončená!",
    reward: "Celková odmena",
    you: "Dostaneš ty (95%)",
    burned: "Spálené navždy (5%)",
    pending: "R₿L na uplatnenie",
    claim_later: "Uplatni všetko z profilu",
    close: "Zavrieť",
    achievements: "Odomknuté úspechy",
    bonus: "bonus",
    next: "Ďalšia kapitola →",
  }, de: { title: "Kapitel abgeschlossen!", reward: "Gesamtbelohnung", you: "Du erhältst (95%)", burned: "Für immer verbrannt (5%)", pending: "Ausstehende R₿L", claim_later: "Fordere alles von deinem Profil an", close: "Schließen", achievements: "Freigeschaltete Erfolge", bonus: "Bonus", next: "Nächstes Kapitel →" }, "zh-CN": { title: "章节完成！", reward: "总奖励", you: "你获得 (95%)", burned: "永久销毁 (5%)", pending: "待领取 R₿L", claim_later: "从你的个人资料中领取全部", close: "关闭", achievements: "解锁成就", bonus: "奖励", next: "下一章节 →" },
};

const ACH_LABEL = {
  daily_student: { es: "Estudiante del día", en: "Daily student", sk: "Študent dňa", de: "Student des Tages", "zh-CN": "今日学员" },
  perfect_week: { es: "Semana perfecta", en: "Perfect week", sk: "Perfektný týždeň", de: "Perfekte Woche", "zh-CN": "完美的一周" },
  quiz_master: { es: "Maestro del quiz", en: "Quiz master", sk: "Majster kvízu", de: "Quiz-Meister", "zh-CN": "问答大师" },
  module_complete: { es: "Maratonista", en: "Module marathon", sk: "Maratónec modulu", de: "Marathonläufer", "zh-CN": "马拉松跑者" },
};

const fmt = (n) => Number(n || 0).toLocaleString("es-ES");

export const RewardModal = ({ open, onClose, result, lessonKey }) => {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !result) return null;

  const reward = result.reward ?? 0;
  const student = result.student ?? Math.floor(reward * 0.95);
  const burn = result.burn ?? Math.floor(reward * 0.05);
  const pending = result.pending_balance ?? 0;
  const achievements = Array.isArray(result.achievements_unlocked) ? result.achievements_unlocked : [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
        onClick={onClose}
        data-testid="reward-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#F5D76E]/30 bg-[#0a0a0f] p-7 shadow-[0_0_60px_rgba(245,215,110,0.35)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-white/40 transition hover:bg-white/10 hover:text-white"
            aria-label="close"
            data-testid="reward-modal-close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.05 }}
              className="rounded-full bg-gradient-to-br from-[#F5D76E] to-orange-500 p-4 shadow-[0_0_40px_rgba(255,140,0,0.55)]"
            >
              <Trophy className="h-8 w-8 text-black" />
            </motion.div>

            <h2 className="mt-5 text-2xl font-black text-white" data-testid="reward-modal-title">
              {t.title}
            </h2>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
              {lessonKey}
            </p>

            <div className="mt-6 w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-white/55">{t.reward}</span>
                <span className="font-mono text-2xl font-black text-[#F5D76E]" data-testid="reward-modal-total">
                  {fmt(reward)} R₿L
                </span>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-1.5 text-emerald-300/90">
                    <Wallet className="h-3.5 w-3.5" /> {t.you}
                  </span>
                  <span className="font-bold text-emerald-300" data-testid="reward-modal-student">
                    {fmt(student)} R₿L
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-1.5 text-orange-300/90">
                    <Flame className="h-3.5 w-3.5" /> {t.burned}
                  </span>
                  <span className="font-bold text-orange-300" data-testid="reward-modal-burn">
                    {fmt(burn)} R₿L
                  </span>
                </div>
              </div>
            </div>

            {achievements.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 w-full rounded-xl border border-violet-400/30 bg-violet-500/[0.08] p-4"
                data-testid="reward-modal-achievements"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-violet-300">
                  ✨ {t.achievements}
                </p>
                <ul className="mt-2 space-y-1.5 text-left">
                  {achievements.map((a) => (
                    <li key={a.code} className="flex items-center gap-2 text-sm text-white" data-testid={`ach-${a.code}`}>
                      <Award className="h-4 w-4 shrink-0 text-violet-300" />
                      <span className="flex-1">{ACH_LABEL[a.code]?.[lang] || a.code}</span>
                      <span className="font-mono text-xs font-bold text-violet-200">
                        +{fmt(a.bonus_rbl)} {t.bonus}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            <div className="mt-5 w-full rounded-xl border border-orange-400/25 bg-orange-500/[0.06] px-4 py-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/65">{t.pending}</span>
                <span className="font-mono text-base font-black text-white" data-testid="reward-modal-pending">
                  {fmt(pending)} R₿L
                </span>
              </div>
              <p className="mt-1 text-[11px] text-white/50">{t.claim_later}</p>
            </div>

            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_24px_rgba(255,140,0,0.45)] transition hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.65)]"
              data-testid="reward-modal-cta"
            >
              <Sparkles className="h-3.5 w-3.5" /> {t.next}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RewardModal;
